import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Plus } from "react-feather";
import { apiErrorMessage, useGet, usePost } from "../../api";
import { IWorkPipelineBlockReason } from "../../interfaces";

interface BlockReasonsModalProps {
  show: boolean;
  onHide: () => void;
  /** Lets the board refresh the reason picker after the catalogue changes. */
  onSaved: () => void;
}

export const BlockReasonsModal: React.FC<BlockReasonsModalProps> = ({
  show,
  onHide,
  onSaved,
}) => {
  const {
    data: reasons,
    loading,
    refetch,
  } = useGet<IWorkPipelineBlockReason[]>(
    "work-pipeline/block-reasons?includeInactive=true",
    show
  );

  const saveReq = usePost<IWorkPipelineBlockReason>();
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const save = async (body: Record<string, unknown>) => {
    setError(null);
    try {
      await saveReq.execute("work-pipeline/block-reasons", body);
      await refetch();
      onSaved();
      return true;
    } catch (err: any) {
      setError(apiErrorMessage(err, "Could not save the block reason."));
      return false;
    }
  };

  const addReason = async () => {
    if (!label.trim()) return;
    const ok = await save({
      label: label.trim(),
      description: description.trim(),
      isActive: true,
    });
    if (ok) {
      setLabel("");
      setDescription("");
    }
  };

  const toggleActive = (reason: IWorkPipelineBlockReason) =>
    save({
      id: reason._id,
      label: reason.label,
      description: reason.description,
      isActive: !reason.isActive,
    });

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Block reasons</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError(null)}
            className="py-2"
          >
            {error}
          </Alert>
        )}

        <Row className="g-2 align-items-end mb-3">
          <Col md={4}>
            <Form.Label className="small mb-1">Label</Form.Label>
            <Form.Control
              size="sm"
              placeholder="e.g. OTP required"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addReason();
              }}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small mb-1">Description</Form.Label>
            <Form.Control
              size="sm"
              placeholder="When should a tester pick this?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Button
              size="sm"
              variant="primary"
              className="w-100"
              disabled={!label.trim() || saveReq.loading}
              onClick={addReason}
            >
              <Plus size={13} className="me-1" /> Add
            </Button>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" size="sm" /> Loading…
          </div>
        ) : !reasons?.length ? (
          <div className="text-muted small">No block reasons yet.</div>
        ) : (
          <Table size="sm" className="align-middle mb-0">
            <thead>
              <tr>
                <th>Label</th>
                <th>Code</th>
                <th>Description</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reasons.map((r) => (
                <tr key={r._id}>
                  <td className="fw-semibold">{r.label}</td>
                  <td>
                    <code className="small">{r.code}</code>
                  </td>
                  <td>
                    <small className="text-muted">{r.description || "—"}</small>
                  </td>
                  <td>
                    <Badge bg={r.isActive ? "success" : "secondary"}>
                      {r.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant={
                        r.isActive ? "outline-secondary" : "outline-success"
                      }
                      disabled={saveReq.loading}
                      onClick={() => toggleActive(r)}
                    >
                      {r.isActive ? "Deactivate" : "Activate"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
